import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { format } from "date-fns";
import { periodRange } from "../notificationCenter/PriceList";
import { SA, StatusPill, TONES, saTableBodySx, saTableHeadSx, saToggleSx } from "./saUi";
import {
  PriceChange,
  PricePeriod,
  formatUnitPrice,
} from "../../services/notificationCenterService";

const day = (iso: string) => format(new Date(iso), "d MMM yyyy");
const dayTime = (iso: string) => format(new Date(iso), "d MMM yyyy, HH:mm");

const parsePrice = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const number = Number(trimmed);
  return Number.isFinite(number) ? number : NaN;
};

// A typed date means the start of that day, here.
const startOfDayIso = (date: string) => new Date(`${date}T00:00:00`).toISOString();

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return format(d, "yyyy-MM-dd");
};

export type PriceSubmit = {
  price: number | null;
  cost?: number | null;
  effectiveFrom?: string;
  note?: string | null;
};

/**
 * Sets a new price, now or from a later date. With `withCost` it also takes
 * what the message costs us; with `allowDefault` the price can be left empty
 * to put the organization back on the default price.
 */
export const PriceForm: React.FC<{
  withCost?: boolean;
  allowDefault?: boolean;
  defaultPrice?: number | null;
  initialCost?: number | null;
  currency: string;
  onSubmit: (value: PriceSubmit) => Promise<void>;
}> = ({ withCost, allowDefault, defaultPrice, initialCost, currency, onSubmit }) => {
  const [price, setPrice] = useState("");
  const [cost, setCost] = useState(initialCost != null ? String(initialCost) : "");
  const [when, setWhen] = useState<"now" | "later">("now");
  const [date, setDate] = useState(tomorrow());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const priceValue = parsePrice(price);
  const costValue = parsePrice(cost);
  const priceError =
    Number.isNaN(priceValue) || (priceValue !== null && (priceValue < 0 || priceValue > 10))
      ? "Between 0 and 10"
      : priceValue === null && !allowDefault
        ? "Required"
        : null;
  const costError =
    Number.isNaN(costValue) || (costValue !== null && (costValue < 0 || costValue > 10)) ? "Between 0 and 10" : null;
  const dateError = when === "later" && (!date || new Date(`${date}T00:00:00`) <= new Date()) ? "Pick a future day" : null;
  const blank = priceValue === null && !allowDefault;

  const submit = async () => {
    if (priceError || costError || dateError || blank) return;
    setBusy(true);
    try {
      await onSubmit({
        price: priceValue,
        ...(withCost ? { cost: costValue } : {}),
        ...(when === "later" ? { effectiveFrom: startOfDayIso(date) } : {}),
        note: note.trim() || null,
      });
      setPrice("");
      setNote("");
      setWhen("now");
    } catch {
      // The caller already showed why; keep what was typed.
    } finally {
      setBusy(false);
    }
  };

  const money = { input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: withCost ? "1fr 1fr" : "1fr" } }}>
        <TextField
          size="small"
          label={allowDefault ? "Their price per message" : "Price per message"}
          placeholder={allowDefault && defaultPrice != null ? `Default ${formatUnitPrice(defaultPrice, currency)}` : "0.050"}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          error={!!price && !!priceError}
          helperText={
            (price && priceError) ||
            (allowDefault ? "Leave empty to put them back on the default price." : "What the organization pays, per delivered message.")
          }
          slotProps={{ ...money, htmlInput: { inputMode: "decimal" } }}
        />
        {withCost && (
          <TextField
            size="small"
            label="Our cost per message"
            placeholder="0.030"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            error={!!costError}
            helperText={costError || "What tw1 charges us. Owners never see it."}
            slotProps={{ ...money, htmlInput: { inputMode: "decimal" } }}
          />
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={when}
          onChange={(_, v) => v && setWhen(v)}
          sx={{ ...saToggleSx, height: 40 }}
        >
          <ToggleButton value="now">Starts now</ToggleButton>
          <ToggleButton value="later">From a date</ToggleButton>
        </ToggleButtonGroup>
        {when === "later" && (
          <TextField
            size="small"
            type="date"
            label="Starts on"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            error={!!dateError}
            helperText={dateError || "At midnight, your time."}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}
        <TextField
          size="small"
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 200 } }}
          sx={{ flex: 1, minWidth: 180 }}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={submit}
          disabled={busy || blank || !!priceError || !!costError || !!dateError}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ textTransform: "none", minHeight: 40, borderRadius: "10px", fontWeight: 600 }}
        >
          {priceValue === null && allowDefault ? "Use default price" : "Set price"}
        </Button>
      </Box>
    </Box>
  );
};

const margin = (price: number | null | undefined, cost: number | null | undefined) =>
  price == null || cost == null ? null : price - cost;

const MarginText: React.FC<{ value: number | null; currency: string }> = ({ value, currency }) =>
  value === null ? (
    <Typography component="span" sx={{ color: SA.textSubtle }}>
      —
    </Typography>
  ) : (
    <Typography
      component="span"
      sx={{ fontSize: "inherit", fontWeight: 600, color: value < 0 ? TONES.red.fg : TONES.green.fg }}
    >
      {formatUnitPrice(value, currency)}
    </Typography>
  );

/** Price periods, newest first: when, price, our cost and the margin. */
export const PriceHistoryTable: React.FC<{ history: PricePeriod[]; currency: string }> = ({ history, currency }) => {
  const now = new Date();
  if (history.length === 0) {
    return <Typography sx={{ fontSize: "0.88rem", color: SA.textMuted }}>No price set yet.</Typography>;
  }
  return (
    <Box sx={{ border: `1px solid ${SA.border}`, borderRadius: "12px", overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 520 }}>
        <TableHead sx={saTableHeadSx}>
          <TableRow>
            <TableCell>Period</TableCell>
            <TableCell>Price</TableCell>
            <TableCell align="right">Charged</TableCell>
            <TableCell align="right">Our cost</TableCell>
            <TableCell align="right">Margin</TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={saTableBodySx}>
          {history.map((period) => {
            const started = new Date(period.from) <= now;
            const ended = !!period.to && new Date(period.to) <= now;
            return (
              <TableRow key={period.from}>
                <TableCell sx={{ color: ended ? `${SA.textMuted} !important` : undefined }}>
                  {periodRange(period)}
                </TableCell>
                <TableCell>
                  {!started ? (
                    <StatusPill tone="amber">Upcoming</StatusPill>
                  ) : ended ? (
                    <StatusPill tone="slate" dot={false}>
                      Past
                    </StatusPill>
                  ) : (
                    <StatusPill tone="green">Now</StatusPill>
                  )}{" "}
                  {period.source === "ORGANIZATION" && (
                    <StatusPill tone="blue" dot={false}>
                      Own price
                    </StatusPill>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                  {formatUnitPrice(period.price, currency)}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatUnitPrice(period.cost, currency)}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  <MarginText value={margin(period.price, period.cost)} currency={currency} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

/** Every price that was set, newest first; a change not started yet can be removed. */
export const PriceChangesTable: React.FC<{
  changes: PriceChange[];
  currency: string;
  channelNames: Record<string, string>;
  withCost?: boolean;
  onDelete: (change: PriceChange) => Promise<void>;
}> = ({ changes, currency, channelNames, withCost, onDelete }) => {
  const [deleting, setDeleting] = useState<string | null>(null);
  if (changes.length === 0) {
    return <Typography sx={{ fontSize: "0.88rem", color: SA.textMuted }}>Nothing set yet.</Typography>;
  }
  return (
    <Box sx={{ border: `1px solid ${SA.border}`, borderRadius: "12px", overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 640 }}>
        <TableHead sx={saTableHeadSx}>
          <TableRow>
            <TableCell>Channel</TableCell>
            <TableCell>Starts</TableCell>
            <TableCell align="right">Price</TableCell>
            {withCost && <TableCell align="right">Our cost</TableCell>}
            <TableCell>Note</TableCell>
            <TableCell>Set</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody sx={saTableBodySx}>
          {changes.map((change) => (
            <TableRow key={change.id}>
              <TableCell sx={{ fontWeight: 600 }}>{channelNames[change.channel] ?? change.channel}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {dayTime(change.effectiveFrom)}{" "}
                {change.scheduled && <StatusPill tone="amber">Scheduled</StatusPill>}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                {change.price === null ? "Default price" : formatUnitPrice(change.price, currency)}
              </TableCell>
              {withCost && (
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatUnitPrice(change.cost, currency)}
                </TableCell>
              )}
              <TableCell sx={{ color: `${SA.textMuted} !important`, maxWidth: 240 }}>{change.note || "—"}</TableCell>
              <TableCell sx={{ color: `${SA.textMuted} !important`, whiteSpace: "nowrap" }}>
                {day(change.createdAt)}
                {change.createdBy ? ` · +${change.createdBy}` : ""}
              </TableCell>
              <TableCell align="right" sx={{ width: 48 }}>
                {change.scheduled && (
                  <Tooltip title="Cancel this scheduled change">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Cancel scheduled price"
                        disabled={deleting === change.id}
                        onClick={async () => {
                          setDeleting(change.id);
                          try {
                            await onDelete(change);
                          } finally {
                            setDeleting(null);
                          }
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
